import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { FlightDetails, ConnectionFlight } from "./types";
import SearchResults from "./SearchResults";
import { fetchFlights, getLastRoute, getDepartureCodes, getArrivalCodes } from "apis/loadsServices";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import Select from "react-select";
import { Id, toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";

// Custom styles for DatePicker
const datePickerStyles = `
  .react-datepicker__day--highlighted {
    background-color: #FFD700 !important;
    color: #000 !important;
  }
  .react-datepicker__day--highlighted:hover {
    background-color: #FFC700 !important;
  }
`;

// Define interfaces for the API responses
interface DepartureCodeResponse {
  departureCode: string;
  departureName: string;
}

interface ArrivalCodeResponse {
  arrivalCode: string;
  arrivalAirportName: string;
}

interface LastRouteResponse {
  lastSearchFlightNumber: string | null;
  lastSearchRoute: {
    source: string;
    destination: string;
    date: string;
  };
}
interface SearchByRouteProps {
  onShowSearchResults?: (show: boolean) => void; // Add callback prop
  onPostRequestSuccess?: () => void; // Add callback for successful post requests
}

const formatDateLocal = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// localStorage utility functions for last search
const LAST_SEARCH_STORAGE_KEY = "staffTraveler_lastSearch";

interface LastSearchData {
  source: string;
  destination: string;
  dates: string[]; // Array of "YYYY-MM-DD" formatted dates
  savedAt: number; // Timestamp for potential cleanup
}

const saveLastSearchToStorage = (source: string, destination: string, dates: string[]): void => {
  try {
    const data: LastSearchData = {
      source,
      destination,
      dates,
      savedAt: Date.now(),
    };
    localStorage.setItem(LAST_SEARCH_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error("Error saving last search to localStorage:", error);
  }
};

const loadLastSearchFromStorage = (): LastSearchData | null => {
  try {
    const stored = localStorage.getItem(LAST_SEARCH_STORAGE_KEY);
    if (!stored) return null;

    const data: LastSearchData = JSON.parse(stored);
    // Validate data structure
    if (data.source && data.destination && Array.isArray(data.dates)) {
      return data;
    }
    return null;
  } catch (error) {
    console.error("Error loading last search from localStorage:", error);
    return null;
  }
};

// localStorage utility functions for recent locations
const RECENT_FROM_LOCATIONS_KEY = "staffTraveler_recentFromLocations";
const RECENT_TO_LOCATIONS_KEY = "staffTraveler_recentToLocations";

interface RecentLocation {
  code: string;
  name: string;
  city?: string;
  country?: string;
  savedAt: number;
}

const saveRecentLocation = (type: "from" | "to", code: string, name: string, city?: string, country?: string): void => {
  try {
    const key = type === "from" ? RECENT_FROM_LOCATIONS_KEY : RECENT_TO_LOCATIONS_KEY;
    const stored = localStorage.getItem(key);
    let recentLocations: RecentLocation[] = stored ? JSON.parse(stored) : [];

    // Remove if already exists (avoid duplicates)
    recentLocations = recentLocations.filter((loc) => loc.code !== code);

    // Add new location to beginning
    recentLocations.unshift({
      code,
      name,
      city,
      country,
      savedAt: Date.now(),
    });

    // Keep only last 5 items
    recentLocations = recentLocations.slice(0, 5);

    localStorage.setItem(key, JSON.stringify(recentLocations));
  } catch (error) {
    console.error(`Error saving recent ${type} location to localStorage:`, error);
  }
};

const loadRecentLocations = (type: "from" | "to"): RecentLocation[] => {
  try {
    const key = type === "from" ? RECENT_FROM_LOCATIONS_KEY : RECENT_TO_LOCATIONS_KEY;
    const stored = localStorage.getItem(key);
    if (!stored) return [];

    const data: RecentLocation[] = JSON.parse(stored);
    // Sort by most recent first (already sorted by unshift, but ensure it)
    return data.sort((a, b) => b.savedAt - a.savedAt).slice(0, 5);
  } catch (error) {
    console.error(`Error loading recent ${type} locations from localStorage:`, error);
    return [];
  }
};

const SearchByRoute: React.FC<SearchByRouteProps> = ({ onShowSearchResults, onPostRequestSuccess }) => {
  const [selectedDeparture, setSelectedDeparture] = useState<"yes" | "no">("no");
  const [selectedArrival, setSelectedArrival] = useState<"yes" | "no">("no");
  // const [stopsFilter, setStopsFilter] = useState<"none" | "one-or-fewer" | null>("none");
  const [stopsFilter, setStopsFilter] = useState<"none" | "one-or-fewer" | "one" | "two" | "three" |null>("none");
  const [fromLocation, setFromLocation] = useState<string>("");
  const [toLocation, setToLocation] = useState<string>("");
  const [date, setDate] = useState<string>(formatDateLocal(new Date(Date.now() + 86400000))); // Default to Tomorrow's date
  const [showResults, setShowResults] = useState(false);
  const [errors, setErrors] = useState<{
    from?: string;
    to?: string;
    date?: string;
  }>({});
  const [flights, setFlights] = useState<FlightDetails[]>([]);
  const [connectionFlights, setConnectionFlights] = useState<ConnectionFlight[]>([]);
  const [isLoadingFlights, setIsLoadingFlights] = useState(false);
  const [departureCodes, setDepartureCodes] = useState<DepartureCodeResponse[]>([]);
  const [arrivalCodes, setArrivalCodes] = useState<ArrivalCodeResponse[]>([]);
  const [isLoadingDepartures, setIsLoadingDepartures] = useState(false);
  const [isLoadingArrivals, setIsLoadingArrivals] = useState(false);
  const [isLoadingLastSearch, setIsLoadingLastSearch] = useState(false);
  const { showLoader, hideLoader } = useGlobalLoader();
  const dateInputRef = useRef<HTMLInputElement | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [PageNumber, setPageNumber] = useState(1);
  const [totalFlights, setTotalFlights] = useState(0);
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [datesInRange, setDatesInRange] = useState<string[]>([]);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Recent locations state
  const [recentFromLocations, setRecentFromLocations] = useState<RecentLocation[]>([]);
  const [recentToLocations, setRecentToLocations] = useState<RecentLocation[]>([]);
  const [isFromCardOpen, setIsFromCardOpen] = useState<boolean>(false);
  const [isToCardOpen, setIsToCardOpen] = useState<boolean>(false);
  const [fromSearchInput, setFromSearchInput] = useState<string>("");
  const [toSearchInput, setToSearchInput] = useState<string>("");

  const flightsPerPage = 9;
  const totalPages = Math.ceil(totalFlights / flightsPerPage);

  const navigate = useNavigate();

  // Create options for react-select with combined code and name - memoized to prevent re-renders
  const departureOptions = useMemo(() => {
    return departureCodes.map((code) => ({
      value: code.departureCode,
      label: `${code.departureCode} - ${code.departureName}`,
      isDisabled: code.departureCode === toLocation,
    }));
  }, [departureCodes, toLocation]);

  const arrivalOptions = useMemo(() => {
    return arrivalCodes.map((code) => ({
      value: code.arrivalCode,
      label: `${code.arrivalCode} - ${code.arrivalAirportName}`,
      isDisabled: code.arrivalCode === fromLocation,
    }));
  }, [arrivalCodes, fromLocation]);

  // Load recent locations from localStorage on component mount
  useEffect(() => {
    const fromLocs = loadRecentLocations("from");
    const toLocs = loadRecentLocations("to");
    setRecentFromLocations(fromLocs);
    setRecentToLocations(toLocs);
  }, []);

  // Refs to track last searched values to prevent duplicate API calls
  const lastFromSearchRef = useRef<string>("");
  const lastToSearchRef = useRef<string>("");

  // Fetch departure codes - memoized with useCallback to prevent infinite loops
  const loadDepartureCodes = useCallback(async (inputValue: string) => {
    const trimmedValue = inputValue.trim();

    if (!trimmedValue) {
      return;
    }

    // Check if we're already loading or if this is a duplicate request
    if (lastFromSearchRef.current === trimmedValue) {
      return;
    }

    // Mark as currently searching before making the call
    const currentSearch = trimmedValue;
    lastFromSearchRef.current = currentSearch;
    // setIsLoadingDepartures(true); // <-- moved here, after all early returns

    try {
      const response = await getDepartureCodes(trimmedValue);
      const newData = response?.data || (Array.isArray(response) ? response : []);

      // If no results, set empty array
      if (!newData || (Array.isArray(newData) && newData.length === 0)) {
        setDepartureCodes([]);
      } else if (lastFromSearchRef.current === currentSearch) {
        setDepartureCodes((prevCodes) => {
          // Compare to avoid unnecessary updates
          const prevDataStr = JSON.stringify(prevCodes);
          const newDataStr = JSON.stringify(newData);
          if (prevDataStr !== newDataStr) {
            return newData;
          }
          return prevCodes;
        });
      }
    } catch (error) {
      console.error("Error fetching departure codes:", error);
      // Only reset ref if this was the current search
      if (lastFromSearchRef.current === currentSearch) {
        lastFromSearchRef.current = "";
      }
      setDepartureCodes([]); // Show no results on error
    } finally {
      // Only clear loading if this is still the current search
      if (lastFromSearchRef.current === currentSearch || lastFromSearchRef.current === "") {
        setIsLoadingDepartures(false);
      }
    }
  }, []);

  // Fetch arrival codes - memoized with useCallback to prevent infinite loops
  const loadArrivalCodes = useCallback(async (inputValue: string) => {
    const trimmedValue = inputValue.trim();

    if (!trimmedValue) {
      return;
    }

    // Check if we're already loading or if this is a duplicate request
    if (lastToSearchRef.current === trimmedValue) {
      return;
    }

    // Mark as currently searching before making the call
    const currentSearch = trimmedValue;
    lastToSearchRef.current = currentSearch;
    // setIsLoadingArrivals(true); // <-- moved here, after all early returns

    try {
      const response = await getArrivalCodes(trimmedValue);
      const newData = response?.data || (Array.isArray(response) ? response : []);

      // If no results, set empty array
      if (!newData || (Array.isArray(newData) && newData.length === 0)) {
        setArrivalCodes([]);
      } else if (lastToSearchRef.current === currentSearch) {
        setArrivalCodes((prevCodes) => {
          // Compare to avoid unnecessary updates
          const prevDataStr = JSON.stringify(prevCodes);
          const newDataStr = JSON.stringify(newData);
          if (prevDataStr !== newDataStr) {
            return newData;
          }
          return prevCodes;
        });
      }
    } catch (error) {
      console.error("Error fetching arrival codes:", error);
      // Only reset ref if this was the current search
      if (lastToSearchRef.current === currentSearch) {
        lastToSearchRef.current = "";
      }
      setArrivalCodes([]); // Show no results on error
    } finally {
      // Only clear loading if this is still the current search
      if (lastToSearchRef.current === currentSearch || lastToSearchRef.current === "") {
        setIsLoadingArrivals(false);
      }
    }
  }, []);

  // Clear "To" field if same value is selected in "From"
  useEffect(() => {
    if (fromLocation && fromLocation === toLocation) {
      setToLocation("");
      setErrors((prev) => ({
        ...prev,
        to: "Departure and arrival locations cannot be the same",
      }));
    }
  }, [fromLocation]);

  // Clear "From" field if same value is selected in "To"
  useEffect(() => {
    if (toLocation && toLocation === fromLocation) {
      setFromLocation("");
      setErrors((prev) => ({
        ...prev,
        from: "Departure and arrival locations cannot be the same",
      }));
    }
  }, [toLocation]);

  // Track previous filter value to detect changes
  // const prevStopsFilter = useRef<"none" | "one-or-fewer" | null>("none");
  const prevStopsFilter = useRef<"none" | "one-or-fewer" | "one" | "two" | "three" | null>("none");
  // Track previous page number to detect page changes
  const prevPageNumber = useRef<number>(1);
  // Track if we're auto-resetting to prevent infinite loops
  const isAutoResetting = useRef<boolean>(false);

  // Handler for stops filter change - sets loading state immediately
  // const handleStopsFilterChange = (filter: "none" | "one-or-fewer" | null) => {
   const handleStopsFilterChange = (filter: "none" | "one-or-fewer" | "one" | "two" | "three" | null) => {
    // Always set the filter
    const filterChanged = hasSearched && prevStopsFilter.current !== filter;

    // If filter changed and user has searched, set loading state immediately (synchronously)
    if (filterChanged) {
      setIsLoadingFlights(true);
    }

    // Update the filter which will trigger the useEffect
    setStopsFilter(filter);
  };

  // Fetch flights when filter changes (reset to page 1 first)
  useEffect(() => {
    // Only fetch if filter actually changed and user has searched before
    if (hasSearched && prevStopsFilter.current !== stopsFilter) {
      // Reset to page 1 if needed - update prevPageNumber to prevent page change effect
      const needsPageReset = PageNumber !== 1;
      if (needsPageReset) {
        prevPageNumber.current = PageNumber; // Mark that we're resetting, not changing
        setPageNumber(1);
      }

      // Use requestAnimationFrame to ensure React has rendered the skeleton before starting API call
      const fetchWithFilter = async () => {
        try {
          // Wait for React to render the skeleton
          await new Promise((resolve) => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                resolve(undefined);
              });
            });
          });

          // Additional small delay to ensure skeleton is visible
          await new Promise((resolve) => setTimeout(resolve, 100));

          await getFlights();

          // Scroll to search results
          setTimeout(() => {
            const searchResultsElement = document.getElementById("search-results");
            if (searchResultsElement) {
              searchResultsElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 100);
        } catch (error) {
          console.error("Error fetching flights for filter:", error);
        }
      };

      fetchWithFilter();
    }
    // Update previous filter value after checking
    if (hasSearched) {
      prevStopsFilter.current = stopsFilter;
    }
  }, [stopsFilter, hasSearched, PageNumber]);

  // Handler for page number change - sets loading state immediately
  const handlePageChange = (page: number) => {
    if (hasSearched && PageNumber !== page) {
      // Set loading state immediately - this happens synchronously
      setIsLoadingFlights(true);
      // Then update the page number which will trigger the useEffect
      setPageNumber(page);
    }
  };

  // Fetch flights when page number changes (but not on initial load or filter change)
  useEffect(() => {
    // Only fetch if page actually changed (not initial load or filter reset)
    if (hasSearched && prevPageNumber.current !== PageNumber && prevPageNumber.current !== null) {
      const fetchFlightsForPage = async () => {
        try {
          // Wait for React to render the skeleton
          await new Promise((resolve) => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                resolve(undefined);
              });
            });
          });

          // Additional small delay to ensure skeleton is visible
          await new Promise((resolve) => setTimeout(resolve, 100));

          await getFlights();

          // Scroll to search results after page change
          setTimeout(() => {
            const searchResultsElement = document.getElementById("search-results");
            if (searchResultsElement) {
              searchResultsElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 100);
        } catch (error) {
          console.error("Error fetching flights for page:", error);
        }
      };
      fetchFlightsForPage();
    }
    // Update previous page number after checking
    if (hasSearched) {
      prevPageNumber.current = PageNumber;
    }
  }, [PageNumber, hasSearched]);

  // Auto-reset filter to "none" when "one-or-fewer" returns no valid connection flights
  useEffect(() => {
    if (hasSearched && stopsFilter === "one-or-fewer" && !isLoadingFlights && !isAutoResetting.current) {
      // Check if there are any valid connection flights (must have at least 2 segments)
      const hasValidConnections = connectionFlights.some((connection) => connection.segments && connection.segments.length >= 2);

      // If no valid connection flights and no direct flights, revert to non-stop
      if (!hasValidConnections && flights.length === 0) {
        // Prevent infinite loops by setting the flag
        isAutoResetting.current = true;
        // Reset filter to "none" and trigger a new search
        prevStopsFilter.current = "one-or-fewer"; // Set previous to current to allow the change
        setStopsFilter("none");
        // Reset the flag after a short delay to allow the filter change to process
        setTimeout(() => {
          isAutoResetting.current = false;
        }, 100);
      }
    }
  }, [flights.length, connectionFlights, stopsFilter, hasSearched, isLoadingFlights]);

  // Clear search function - resets all fields and search state
  const handleClearSearch = () => {
    // Clear form fields
    setFromLocation("");
    setToLocation("");
    setSelectedDates([]);
    setDatesInRange([]);
    setDate(formatDateLocal(new Date(Date.now() + 86400000))); // Reset to tomorrow

    // Close cards and clear search inputs
    setIsFromCardOpen(false);
    setIsToCardOpen(false);
    setFromSearchInput("");
    setToSearchInput("");

    // Clear search results
    setFlights([]);
    setConnectionFlights([]);
    setTotalFlights(0);
    setShowResults(false);

    // Reset search state
    setHasSearched(false);
    setPageNumber(1);
    prevPageNumber.current = 1;
    prevStopsFilter.current = "none";

    // Reset filters
    setStopsFilter("none");
    setSelectedDeparture("no");
    setSelectedArrival("no");

    // Clear errors
    setErrors({});

    // Hide results
    onShowSearchResults?.(false);
  };

  // Optimized last search function - loads from localStorage first, falls back to API
  const handleLastSearchClick = async () => {
    setIsLoadingLastSearch(true);
    setIsLoadingFlights(true); // Show skeleton for search

    try {
      // First check localStorage
      let lastSearchData: LastSearchData | null = loadLastSearchFromStorage();

      // If not found in localStorage, fetch from API as fallback
      if (!lastSearchData) {
        showLoader(); // Show global loader for API call
        try {
          const response = await getLastRoute();
          const lastRouteData: LastRouteResponse = response.data || response;

          if (lastRouteData && lastRouteData.lastSearchRoute) {
            const { source, destination, date: lastDate } = lastRouteData.lastSearchRoute;

            // Convert single date to array format for storage
            lastSearchData = {
              source,
              destination,
              dates: [lastDate],
              savedAt: Date.now(),
            };

            // Save to localStorage for future use
            saveLastSearchToStorage(source, destination, [lastDate]);
          } else {
            console.log("No last search data available");
            hideLoader();
            setIsLoadingLastSearch(false);
            setIsLoadingFlights(false);
            return;
          }
        } catch (error) {
          console.error("Error fetching last search from API:", error);
          hideLoader();
          setIsLoadingLastSearch(false);
          setIsLoadingFlights(false);
          return;
        } finally {
          hideLoader();
        }
      }

      // Populate fields with stored data
      if (lastSearchData) {
        const { source, destination, dates } = lastSearchData;

        // Set locations
        setFromLocation(source);
        setToLocation(destination);

        // Convert date strings to Date objects for calendar
        const dateObjects = dates
          .map((dateStr) => {
            const date = new Date(dateStr);
            // Validate date
            if (isNaN(date.getTime())) {
              return null;
            }
            return date;
          })
          .filter((date): date is Date => date !== null);

        // Set dates
        setSelectedDates(dateObjects);
        setDatesInRange(dates);

        // Set date state for backward compatibility (use first date if available)
        if (dates.length > 0) {
          setDate(dates[0]);
        }

        // Clear any errors
        setErrors({});

        // Set search state
        setHasSearched(true);
        prevPageNumber.current = 1;
        setPageNumber(1);

        // Wait for state updates to complete, then auto-execute search with direct values
        await new Promise((resolve) => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              resolve(undefined);
            });
          });
        });

        // Call getFlights with direct values to ensure they're used
        await getFlights({
          source,
          destination,
          dates,
          pageNumber: 1,
        });

        // Scroll to search results
        setTimeout(() => {
          const searchResultsElement = document.getElementById("search-results");
          if (searchResultsElement) {
            searchResultsElement.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 100);
      }
    } catch (error) {
      console.error("Error loading last search:", error);
    } finally {
      setIsLoadingLastSearch(false);
      // Note: isLoadingFlights will be set to false by getFlights() finally block
    }
  };

  const getFlights = async (overrideParams?: { source?: string; destination?: string; dates?: string[]; pageNumber?: number }) => {
    // Set loading state - will be reset in finally block
    setIsLoadingFlights(true);
    try {
      // Map stops filter to API parameters
      let Stops: number | undefined;
      let IncludeDirect: boolean | undefined;
      let IncludeConnections: boolean | undefined;

      if (stopsFilter === "none") {
        IncludeDirect = true;
        IncludeConnections = false;
        Stops = 0;
      } else if (stopsFilter === "one-or-fewer") {
        IncludeDirect = false;
        IncludeConnections = true;
      }else if (stopsFilter === "one") {
        Stops = 1;
      } else if (stopsFilter === "two") {
        Stops = 2;
      } else if (stopsFilter === "three") {
        Stops = 3;
      } else {
        // Default: non-stop only (since default is "none")
        IncludeDirect = true;
        IncludeConnections = false;
      }

      // Use override params if provided, otherwise use state values
      const sourceValue = overrideParams?.source ?? fromLocation;
      const destinationValue = overrideParams?.destination ?? toLocation;
      const datesValue = overrideParams?.dates ?? datesInRange;
      const pageNumberValue = overrideParams?.pageNumber ?? PageNumber;

      // Prepare params as plain object
      const params = {
        Source: sourceValue,
        Destination: destinationValue,
        IsNearbyDeparture: selectedDeparture === "yes",
        IsNearbyArrival: selectedArrival === "yes",
        Dates: datesValue,
        PageNumber: pageNumberValue,
        PageSize: flightsPerPage,
        IncludeDirect,
        IncludeConnections,
        ...(Stops !== undefined ? { Stops } : {}),
      };

      console.log("Sending params:", params);

      // Fetch flights (GET request)
      const response = await fetchFlights(params);
      const rawFlightData = response.data.items || [];
      const connectionsData = (response.data.connections || []) as ConnectionFlight[];

      // Check if we requested connections but got direct flights instead
      // This happens when API returns direct flights in items array when IncludeConnections: true
      if (stopsFilter === "one-or-fewer" && connectionsData.length === 0 && rawFlightData.length > 0) {
        // Check if rawFlightData contains direct flights (stops: 0 or intermediateAirports: [])
        // The API response has 'stops' field in the raw data
        const hasDirectFlights = rawFlightData.some((flight: any) => {
          return (
            flight.stops === 0 ||
            flight.stops === undefined ||
            (flight.intermediateAirports && Array.isArray(flight.intermediateAirports) && flight.intermediateAirports.length === 0)
          );
        });

        // If we got direct flights instead of connections, we need to revert to non-stop
        if (hasDirectFlights && !isAutoResetting.current) {
          isAutoResetting.current = true;
          prevStopsFilter.current = "one-or-fewer";
          setStopsFilter("none");
          // Don't process this response, let the filter change trigger a new search
          setIsLoadingFlights(false);
          setTimeout(() => {
            isAutoResetting.current = false;
          }, 100);
          return;
        }
      }

      const flightData = rawFlightData as FlightDetails[];

      // Generate composite IDs for direct flights
      const flightsWithCompositeIds = flightData.map((flight) => {
        const baseId = flight.id ?? `${Date.now()}-${Math.random()}`;
        const scheduleDate = flight.scheduleDate || flight.date;
        const compositeId = scheduleDate ? `${baseId}_${scheduleDate.replace(/[^0-9]/g, "")}` : String(baseId);

        return {
          ...flight,
          compositeId,
        };
      });

      // Generate composite IDs for connection flights
      const connectionsWithCompositeIds = connectionsData.map((connection) => {
        // Generate composite IDs for each segment
        const segmentsWithIds = connection.segments.map((segment) => {
          const baseId = segment.id ?? `${Date.now()}-${Math.random()}`;
          const departureTime = segment.departureTime;
          const compositeId = departureTime ? `${baseId}_${departureTime.replace(/[^0-9]/g, "")}` : String(baseId);

          return {
            ...segment,
            compositeId,
          };
        });

        // Generate composite ID for the connection flight itself
        const firstSegment = segmentsWithIds[0];
        const connectionCompositeId = firstSegment?.compositeId
          ? `connection_${firstSegment.compositeId}_${connection.overallDepartureTime.replace(/[^0-9]/g, "")}`
          : `connection_${Date.now()}_${Math.random()}`;

        return {
          ...connection,
          segments: segmentsWithIds,
          compositeId: connectionCompositeId,
        };
      });

      setFlights(flightsWithCompositeIds);
      setConnectionFlights(connectionsWithCompositeIds);

      // Use data.total for all filters (direct flights and connection flights)
      if (response.data.total !== undefined) {
        setTotalFlights(response.data.total);
      } else {
        // Fallback: estimate based on current page results
        const estimatedTotal =
          flightData.length + connectionsData.length < flightsPerPage
            ? (PageNumber - 1) * flightsPerPage + flightData.length + connectionsData.length
            : PageNumber * flightsPerPage;
        setTotalFlights(estimatedTotal);
      }

      setShowResults(flightData.length > 0 || connectionsData.length > 0);
      console.log("Flight data:", flightData);
      console.log("Connection data:", connectionsData);
    } catch (err: any) {
      const apiMessage = err.response?.data?.message;
      const id: Id = "flight-error";
      if (!toast.isActive(id)) {
        toast.error(apiMessage || "Failed to fetch flights.", { toastId: id });
      }
      setFlights([]);
      setConnectionFlights([]);
      setTotalFlights(0);
      setShowResults(false);
    } finally {
      setIsLoadingFlights(false);
    }
  };

  const handleSearchClick = async () => {
    setHasSearched(true);
    prevPageNumber.current = 1; // Reset prev page number for new search
    setPageNumber(1); // Reset to first page on new search
    // Simple validation check when the search button is clicked
    const newErrors: typeof errors = {};
    if (!fromLocation) newErrors.from = "*Please select a departure location.";
    if (!toLocation) newErrors.to = "*Please select an arrival location.";
    if (selectedDates.length === 0) newErrors.date = "*Please select at least one date.";
    if (fromLocation === toLocation && fromLocation) {
      newErrors.from = "Departure and arrival locations cannot be the same.";
      newErrors.to = "Departure and arrival locations cannot be the same.";
    }
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    try {
      // Set loading state first to show skeleton
      setIsLoadingFlights(true);
      // Small delay to ensure skeleton renders
      await new Promise((resolve) => setTimeout(resolve, 50));
      await getFlights();

      // Save search data to localStorage after successful search
      if (fromLocation && toLocation && datesInRange.length > 0) {
        saveLastSearchToStorage(fromLocation, toLocation, datesInRange);
      }

      // Scroll to search results after a brief delay to ensure content is rendered
      setTimeout(() => {
        const searchResultsElement = document.getElementById("search-results");
        if (searchResultsElement) {
          searchResultsElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);
    } catch (error) {
      console.error("Error fetching flights:", error);
    }
  };

  const getDatesBetween = (start: Date, end: Date) => {
    const dates: string[] = [];
    const current = new Date(start);
    while (current <= end) {
      dates.push(formatDateLocal(current));
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      const dateStr = formatDateLocal(date);
      const dateIndex = selectedDates.findIndex((d) => formatDateLocal(d) === dateStr);

      if (dateIndex > -1) {
        // Date already selected, remove it
        const newDates = selectedDates.filter((_, index) => index !== dateIndex);
        setSelectedDates(newDates);
      } else {
        // Add new date
        const newDates = [...selectedDates, date].sort((a, b) => a.getTime() - b.getTime());
        setSelectedDates(newDates);
      }

      // Clear date error when a date is selected
      if (errors.date) {
        setErrors((prev) => ({ ...prev, date: undefined }));
      }
    }
  };

  const handleSaveDates = async () => {
    const formattedDates = selectedDates.map((d) => formatDateLocal(d));
    setDatesInRange(formattedDates);
    setIsCalendarOpen(false);

    // Auto-trigger search if all fields are filled
    if (fromLocation && toLocation && selectedDates.length > 0) {
      setTimeout(async () => {
        try {
          // Set search state
          setHasSearched(true);
          prevPageNumber.current = 1;
          setPageNumber(1);

          // Clear any previous errors
          setErrors({});

          // Set loading state first to show skeleton
          setIsLoadingFlights(true);
          // Small delay to ensure skeleton renders
          await new Promise((resolve) => setTimeout(resolve, 50));

          // Call getFlights with override params to ensure dates are passed
          await getFlights({
            source: fromLocation,
            destination: toLocation,
            dates: formattedDates,
            pageNumber: 1,
          });

          // Save search data to localStorage after successful search
          saveLastSearchToStorage(fromLocation, toLocation, formattedDates);

          // Scroll to search results after a brief delay to ensure content is rendered
          setTimeout(() => {
            const searchResultsElement = document.getElementById("search-results");
            if (searchResultsElement) {
              searchResultsElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 100);
        } catch (error) {
          console.error("Error fetching flights:", error);
        }
      }, 100);
    }
  };

  const clearAllDates = () => {
    setSelectedDates([]);
    setDatesInRange([]);
  };

  // Location Search Card Component
  interface LocationSearchCardProps {
    type: "from" | "to";
    value: string;
    onChange: (code: string, name: string) => void;
    recentLocations: RecentLocation[];
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
    placeholder: string;
    searchInput: string;
    setSearchInput: (value: string) => void;
    options: Array<{ value: string; label: string; code?: string; name?: string; isDisabled?: boolean }>;
    isLoading: boolean;
    label: string;
    onSearch: (inputValue: string) => Promise<void> | void;
  }

  const LocationSearchCard: React.FC<LocationSearchCardProps> = ({
    type,
    value,
    onChange,
    recentLocations,
    isOpen,
    onClose,
    placeholder,
    searchInput,
    setSearchInput,
    options,
    isLoading,
    label,
    onSearch,
  }) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    // Track last searched value to prevent duplicate calls
    const lastSearchedRef = useRef<string>("");
    // Track if a search is currently in progress to prevent overlapping calls
    const isSearchingRef = useRef<boolean>(false);
    // Ref to hold debounce timer id so we can clear it reliably
    const debounceTimerRef = useRef<number | null>(null);

    // Close on outside click
    useEffect(() => {
      if (!isOpen) return;

      const handleClickOutside = (event: MouseEvent) => {
        if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
          onClose();
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isOpen, onClose]);

    // Focus search input when card opens and reset last searched value
    useEffect(() => {
      if (isOpen && searchInputRef.current) {
        // Reset when card opens so user can search again
        lastSearchedRef.current = "";
        // clear any pending timer
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 100);
      } else if (!isOpen) {
        // Reset when card closes
        lastSearchedRef.current = "";
        isSearchingRef.current = false;
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }
      }
    }, [isOpen]);

    const handleSelectLocation = (code: string, name: string) => {
      onChange(code, name);
      setSearchInput("");
      // clear any pending debounce when selecting
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      onClose();
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      // Update input immediately for responsive feel
      setSearchInput(inputValue);
      // Reset last searched when input is cleared
      if (inputValue.trim().length === 0) {
        lastSearchedRef.current = "";
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }
      }
    };

    // Debounced search effect (robust, clears/awaits properly)
    useEffect(() => {
      // If card is closed, ensure we clean up state and timers
      if (!isOpen) {
        lastSearchedRef.current = "";
        isSearchingRef.current = false;
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }
        return;
      }

      const trimmedInput = searchInput.trim();

      // Don't search if input is empty
      if (trimmedInput.length === 0) {
        lastSearchedRef.current = "";
        isSearchingRef.current = false;
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }
        return;
      }

      // Don't search if we already searched for this exact value
      if (lastSearchedRef.current === trimmedInput) {
        return;
      }

      // Clear any existing timer and set a fresh one
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      debounceTimerRef.current = window.setTimeout(async () => {
        // If value changed while waiting, bail out
        if (searchInput.trim() !== trimmedInput) {
          debounceTimerRef.current = null;
          return;
        }

        // If already searching, don't overlap
        if (isSearchingRef.current) {
          debounceTimerRef.current = null;
          return;
        }

        isSearchingRef.current = true;
        lastSearchedRef.current = trimmedInput;

        try {
          const result = onSearch(trimmedInput);
          if (result && typeof (result as Promise<any>).then === "function") {
            await (result as Promise<any>);
          }
        } catch (error) {
          // Reset last searched on error so user can retry
          if (lastSearchedRef.current === trimmedInput) {
            lastSearchedRef.current = "";
          }
          console.error("Error in onSearch:", error);
        } finally {
          isSearchingRef.current = false;
          debounceTimerRef.current = null;
        }
      }, 500);

      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }
      };
    }, [searchInput, isOpen, onSearch]);

    if (!isOpen) return null;

    const showRecentLocations = searchInput.trim().length === 0 && recentLocations.length > 0;
    const showSearchResults = searchInput.trim().length > 0 && options.length > 0;

    return (
      <div
        ref={cardRef}
        className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[600px] w-full overflow-hidden flex flex-col"
      >
        {/* Header with Close Button */}
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-700">{type === "from" ? "Select Departure Airport" : "Select Arrival Airport"}</p>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <i className="st-close text-lg"></i>
          </button>
        </div>

        {/* Search Input Section - At Top */}
        <div className="p-4 border-b border-gray-100">
          <div className="relative">
            <input
              ref={searchInputRef}
              type="text"
              value={searchInput}
              onChange={handleSearchChange}
              placeholder={placeholder}
              className="w-full p-3 pr-10 border border-gray-200 rounded-lg bg-lightblue-100 text-sm text-grease-100 focus:outline-none focus:ring-2 focus:ring-royalblue-100 transition-all duration-150"
              autoComplete="off"
            />
            {isLoading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="w-5 h-5 border-2 border-royalblue-100 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </div>
        </div>

        {/* Content Section - Recent Locations or Search Results */}
        <div className="overflow-y-auto flex-1 max-h-[450px]">
          {/* Recent Locations Section - Show when input is empty */}
          {showRecentLocations && (
            <div>
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-600 uppercase">
                  {type === "from" ? "Recent Airports" : "Recent Destinations"}
                </p>
              </div>
              <div className="divide-y divide-gray-100">
                {recentLocations.map((location) => (
                  <button
                    key={location.code}
                    type="button"
                    onClick={() => handleSelectLocation(location.code, location.name)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900">{location.code}</span>
                        <span className="text-sm text-gray-700">{location.name}</span>
                      </div>
                      {(location.city || location.country) && (
                        <p className="text-xs text-gray-500 mt-1">{[location.city, location.country].filter(Boolean).join(", ")}</p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Search Results - Show when typing */}
          {showSearchResults && (
            <div>
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-600 uppercase">Search Results</p>
              </div>
              <div className="divide-y divide-gray-100">
                {options
                  .filter((option) => !option.isDisabled)
                  .map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        const name = option.label.includes(" - ") ? option.label.split(" - ")[1] : option.label;
                        handleSelectLocation(option.value, name);
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900">{option.value}</span>
                        <span className="text-sm text-gray-700">
                          {option.label.includes(" - ") ? option.label.split(" - ")[1] : option.label}
                        </span>
                      </div>
                    </button>
                  ))}
              </div>
            </div>
          )}

          {/* No Results Message */}
          {searchInput.trim().length > 0 && !isLoading && options.length === 0 && (
            <div className="text-center py-8 text-gray-500 text-sm">No airports found</div>
          )}
        </div>
      </div>
    );
  };

  const searchResults = useMemo(() => {
    if (!hasSearched) return null;
    onShowSearchResults?.(true);
    return (
      <div id="search-results">
        <SearchResults
          flights={flights}
          connectionFlights={connectionFlights}
          stopsFilter={stopsFilter}
          onStopsFilterChange={handleStopsFilterChange}
          isLoadingFlights={isLoadingFlights}
          show={true}
          searchDate={date}
          selectedDates={datesInRange}
          onPostRequestSuccess={onPostRequestSuccess}
        />
        {totalPages > 1 && (
          <div style={{ marginTop: 20, justifyItems: "center" }}>
            <p>
              Page: {PageNumber} of {totalPages}
            </p>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              {[...Array(totalPages)]
                .map((_, i) => i + 1)
                .filter((page) => {
                  const start = Math.max(PageNumber - 2, 1);
                  const end = Math.min(PageNumber + 2, totalPages);
                  return page >= start && page <= end;
                })
                .map((page) => (
                  <button
                    key={page}
                    onClick={() => {
                      handlePageChange(page);
                    }}
                    style={{
                      padding: "6px 12px",
                      backgroundColor: PageNumber === page ? "#304cb2" : "#f0f0f0",
                      color: PageNumber === page ? "#fff" : "#000",
                      border: "none",
                      borderRadius: "4px",
                      cursor: PageNumber === page ? "default" : "pointer",
                      opacity: PageNumber === page ? 1 : 0.8,
                    }}
                    disabled={PageNumber === page}
                  >
                    {page}
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>
    );
  }, [
    hasSearched,
    flights,
    connectionFlights,
    stopsFilter,
    onShowSearchResults,
    PageNumber,
    totalPages,
    totalFlights,
    date,
    datesInRange,
    onPostRequestSuccess,
  ]);

  return (
    <div className="mt-0 bg-[245,245,245] px-4">
      <style>{datePickerStyles}</style>
      <ToastContainer />

      <div className="bg-white rounded-lg rounded-tl-none p-4">
        <div className="flex items-center justify-between pb-4 border-b border-lightgray-50">
          <p className="text-grease-100 text-sm font-medium">Search by Route</p>
          <div className="flex gap-2">
            <button className="flex gap-2 p-3 border border-[#FFBC00] rounded-lg" onClick={() => navigate('/favourites')} type="button">
              <svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-red-600">
                <path
                  d="M12.3799 0.149414C14.8323 0.149494 16.76 2.1358 17.0117 4.87012L17.0322 5.06055C17.0509 5.34293 17.0462 5.8953 16.8623 6.67773C16.5199 8.15991 15.703 9.52545 14.5283 10.5869L14.5273 10.5879L8.64844 15.834L8.54785 15.9238L8.44824 15.833L2.66211 10.5869C1.48739 9.52538 0.670474 8.15988 0.328125 6.67773C0.0876615 5.67947 0.152851 5.03447 0.176758 4.88086C0.423822 2.1401 2.37313 0.149415 4.82715 0.149414C6.34973 0.149414 7.71113 0.918999 8.56641 2.21582C9.41377 0.949865 10.8432 0.149414 12.3799 0.149414ZM12.3809 1.06348C10.9 1.06348 9.57619 1.97344 8.98438 3.37207L8.9834 3.37305L8.68555 4.06152L8.54785 4.37891L8.41016 4.06152L8.1123 3.37305V3.37207C7.50155 1.93423 6.23117 1.0636 4.80859 1.06348C2.87685 1.06348 1.30723 2.69661 1.10742 4.96387V5L1.10547 5.0127C1.1054 5.01314 1.10469 5.01427 1.10449 5.01562C1.10404 5.01865 1.10343 5.02412 1.10254 5.03125C1.10076 5.04551 1.09896 5.06832 1.09668 5.09863C1.09212 5.15934 1.08748 5.2517 1.09082 5.37402C1.09585 5.55777 1.1175 5.80941 1.17773 6.12305L1.25195 6.45801L1.25293 6.45898H1.25195C1.5607 7.74926 2.2704 8.93277 3.30957 9.88086L8.54883 14.6455L13.8984 9.88086C14.9366 8.95151 15.6453 7.76836 15.9541 6.45996C16.0634 5.98697 16.0951 5.6208 16.0996 5.37305C16.1019 5.24919 16.0977 5.1546 16.0928 5.09082C16.0903 5.05896 16.0879 5.03443 16.0859 5.01758C16.085 5.00905 16.0837 5.00262 16.083 4.99707C16.0827 4.99456 16.0823 4.99102 16.082 4.98828V4.98242L16.0586 4.76855C15.7837 2.57934 14.2521 1.0637 12.3809 1.06348Z"
                  fill="currentColor"
                  stroke="currentColor"
                  strokeWidth="0.3"
                />
              </svg>
              <p className="font-semibold text-[#FFBC00] text-xs">Favourites</p>
            </button>
            <button className="flex gap-2 p-3 border border-[#FFBC00] rounded-lg" onClick={handleClearSearch} type="button">
              <i className="st-close text-[#FFBC00]"></i>
              <p className="font-semibold text-[#FFBC00] text-xs">Clear</p>
            </button>
            <button
              className="flex gap-2 p-3 border border-[#FFBC00] rounded-lg"
              onClick={handleLastSearchClick}
              disabled={isLoadingLastSearch}
              type="button"
            >
              <i className="st-previous text-[#FFBC00]"></i>
              <p className="font-semibold text-[#FFBC00] text-xs">{isLoadingLastSearch ? "Loading..." : "Last Search"}</p>
            </button>
          </div>
        </div>
        <div>
          <div className="grid lg:grid-cols-5 md:grid-cols-3 sm:grid-cols-2 gap-6 my-4">
            {/* From Field - Clickable Input with Card */}
            <div className="relative">
              <label className="block mb-2 text-sm text-lightgray-200">From</label>
              <div className="relative">
                <input
                  type="text"
                  readOnly
                  value={
                    fromLocation
                      ? (() => {
                          const option = departureOptions.find((option) => option.value === fromLocation);
                          if (option) {
                            return option.label;
                          }
                          // Fallback: check recent locations
                          const recent = recentFromLocations.find((loc) => loc.code === fromLocation);
                          if (recent) {
                            return `${recent.code} - ${recent.name}`;
                          }
                          return fromLocation;
                        })()
                      : ""
                  }
                  onClick={() => setIsFromCardOpen(true)}
                  placeholder="Select departure location"
                  className="w-full p-4 pr-10 border-none rounded-lg bg-lightblue-100 text-sm text-grease-100 cursor-pointer focus:outline-none focus:ring-2 focus:ring-royalblue-100"
                />
                {fromLocation && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFromLocation("");
                      setIsFromCardOpen(false);
                      setFromSearchInput("");
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <i className="st-close text-lg"></i>
                  </button>
                )}

                {/* Location Search Card */}
                <LocationSearchCard
                  type="from"
                  value={fromLocation}
                  onChange={(code, name) => {
                    setFromLocation(code);
                    setErrors((prev) => ({ ...prev, from: undefined }));

                    // Save to recent locations
                    saveRecentLocation("from", code, name);
                    const updatedRecent = loadRecentLocations("from");
                    setRecentFromLocations(updatedRecent);

                    // Check if the same value is selected in "To" field
                    if (code === toLocation) {
                      setToLocation("");
                      setErrors((prev) => ({
                        ...prev,
                        to: "Departure and arrival locations cannot be the same",
                      }));
                    }

                    // Close from card
                    setIsFromCardOpen(false);
                    setFromSearchInput("");

                    // Auto-open "to" card after 100ms
                    setTimeout(() => {
                      setIsToCardOpen(true);
                    }, 100);
                  }}
                  recentLocations={recentFromLocations}
                  isOpen={isFromCardOpen}
                  onOpen={() => setIsFromCardOpen(true)}
                  onClose={() => {
                    setIsFromCardOpen(false);
                    setFromSearchInput("");
                  }}
                  placeholder="Search airports..."
                  searchInput={fromSearchInput}
                  setSearchInput={setFromSearchInput}
                  options={departureOptions}
                  isLoading={isLoadingDepartures}
                  label="From"
                  onSearch={loadDepartureCodes}
                />
              </div>
              {errors.from && <p className="text-red-500 text-sm">{errors.from}</p>}
            </div>

            {/* To Field - Clickable Input with Card */}
            <div className="relative">
              <label className="block mb-2 text-sm text-lightgray-200">To</label>
              <div className="relative">
                <input
                  type="text"
                  readOnly
                  value={
                    toLocation
                      ? (() => {
                          const option = arrivalOptions.find((option) => option.value === toLocation);
                          if (option) {
                            return option.label;
                          }
                          // Fallback: check recent locations
                          const recent = recentToLocations.find((loc) => loc.code === toLocation);
                          if (recent) {
                            return `${recent.code} - ${recent.name}`;
                          }
                          return toLocation;
                        })()
                      : ""
                  }
                  onClick={() => setIsToCardOpen(true)}
                  placeholder="Select arrival location"
                  className="w-full p-4 pr-10 border-none rounded-lg bg-lightblue-100 text-sm text-grease-100 cursor-pointer focus:outline-none focus:ring-2 focus:ring-royalblue-100"
                />
                {toLocation && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setToLocation("");
                      setIsToCardOpen(false);
                      setToSearchInput("");
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <i className="st-close text-lg"></i>
                  </button>
                )}

                {/* Location Search Card */}
                <LocationSearchCard
                  type="to"
                  value={toLocation}
                  onChange={(code, name) => {
                    setToLocation(code);
                    setErrors((prev) => ({ ...prev, to: undefined }));

                    // Save to recent locations
                    saveRecentLocation("to", code, name);
                    const updatedRecent = loadRecentLocations("to");
                    setRecentToLocations(updatedRecent);

                    // Check if the same value is selected in "From" field
                    if (code === fromLocation) {
                      setFromLocation("");
                      setErrors((prev) => ({
                        ...prev,
                        from: "Departure and arrival locations cannot be the same",
                      }));
                    }

                    // Close to card
                    setIsToCardOpen(false);
                    setToSearchInput("");
                    lastToSearchRef.current = ""; // Reset last searched value

                    // Auto-open date picker after 100ms
                    setTimeout(() => {
                      setIsCalendarOpen(true);
                    }, 100);
                  }}
                  recentLocations={recentToLocations}
                  isOpen={isToCardOpen}
                  onOpen={() => setIsToCardOpen(true)}
                  onClose={() => {
                    setIsToCardOpen(false);
                    setToSearchInput("");
                    lastToSearchRef.current = ""; // Reset last searched value
                  }}
                  placeholder="Search airports..."
                  searchInput={toSearchInput}
                  setSearchInput={setToSearchInput}
                  options={arrivalOptions}
                  isLoading={isLoadingArrivals}
                  label="To"
                  onSearch={loadArrivalCodes}
                />
              </div>
              {errors.to && <p className="text-red-500 text-sm">{errors.to}</p>}
            </div>

            {/* Date Input */}
            {/* <div>
              <label className="block mb-2 text-sm text-lightgray-200">
                Date
              </label>
              <div className="relative">
                <input
                  ref={dateInputRef}
                  type="date"
                  className="block w-full p-4 pr-10 border-none rounded-lg bg-lightblue-100 text-sm text-grease-100 placeholder-gray-400 focus:outline-none"
                  value={date}
                  min={formatDateLocal(new Date())}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const typed = e.target.value;
                    const [year, month, day] = typed.split("-");

                    if (year.length > 4) {
                      return;
                    }

                    setDate(typed);
                    if (errors.date) {
                      setErrors((prev) => ({ ...prev, date: undefined }));
                    }
                  }}
                />
                <button
                  className="absolute inset-y-0 end-0 flex items-center z-20 px-3 cursor-pointer text-gray-400 rounded-e-md focus:outline-none"
                  onClick={() => dateInputRef.current?.showPicker()}
                  type="button"
                >
                  <i className="st-calendar text-lightgray-200"></i>
                </button>
              </div>

              {errors.date && (
                <p className="text-red-500 text-sm">{errors.date}</p>
              )}
            </div> */}

            <div className="flex flex-col">
              <label className="block mb-2 text-sm text-lightgray-200">Select Dates</label>
              <div className="relative">
                <DatePicker
                  selected={null}
                  onChange={handleDateChange}
                  onCalendarClose={() => setIsCalendarOpen(false)}
                  onCalendarOpen={() => setIsCalendarOpen(true)}
                  open={isCalendarOpen}
                  onClickOutside={() => setIsCalendarOpen(false)}
                  shouldCloseOnSelect={false}
                  minDate={new Date()}
                  placeholderText={`${datesInRange.length > 0 ? `Selected Dates (${datesInRange.length})` : "Select travel dates"}`}
                  className={`block w-full p-4 pr-10 border-none rounded-lg bg-lightblue-100 text-sm text-grease-100 ${datesInRange.length > 0 ? "placeholder-black" : "placeholder-gray-400"} focus:outline-none cursor-pointer`}
                  highlightDates={selectedDates}
                  inline={false}
                  // calendarClassName="text-base [&_.react-datepicker\_\_day]:w-10 [&_.react-datepicker\_\_day]:h-8 [&_.react-datepicker\_\_day]:leading-10 [&_.react-datepicker\_\_day-name]:w-10 [&_.react-datepicker\_\_day-name]:h-10 [&_.react-datepicker\_\_day-name]:leading-10 [&_.react-datepicker\_\_current-month]:text-lg [&_.react-datepicker\_\_current-month]:mb-2"
                  wrapperClassName="w-full"
                  onInputClick={() => setIsCalendarOpen(true)}
                >
                  <div className="flex justify-between w-full px-2 pt-2">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        clearAllDates();
                      }}
                      className="px-4 py-2 text-sm text-red-600 border border-red-600 rounded-lg hover:bg-red-50"
                      type="button"
                    >
                      Clear All
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleSaveDates();
                      }}
                      className="px-4 py-2 text-sm text-white bg-[#FFBC00] rounded-lg hover:bg-[#e6a800]"
                      type="button"
                    >
                      Save ({selectedDates.length})
                    </button>
                  </div>
                </DatePicker>
                {datesInRange.length > 0 && (
                  <button
                    onClick={() => setIsCalendarOpen(true)}
                    className="absolute inset-y-0 end-0 flex items-center z-20 px-3  text-xl cursor-pointer text-gray-400 hover:text-blue-600 rounded-e-md focus:outline-none"
                    type="button"
                    title="Edit dates"
                  >
                    <i className="st-calendar text-lightgray-200"></i>
                  </button>
                )}
              </div>

              {errors.date && <p className="text-red-500 text-sm mt-1">{errors.date}</p>}
            </div>

            {/* Include nearby Departure Airports */}
            <div className="flex flex-col gap-2">
              <label className="block mb-2 text-sm text-lightgray-200">Include nearby Departure Airports</label>
              <div className="flex bg-lightblue-100 p-2 rounded-md overflow-hidden w-full -mt-2">
                <button
                  className={`p-2 rounded-md text-sm transition-colors w-full ${
                    selectedDeparture === "yes" ? "bg-white text-[#FFBC00] shadow-lg shadow-blue-100" : "text-gray-500 hover:text-[#FFBC00]"
                  }`}
                  onClick={() => setSelectedDeparture("yes")}
                >
                  Yes
                </button>
                <button
                  className={`p-2 rounded-md text-sm transition-colors w-full ${
                    selectedDeparture === "no" ? "bg-white text-[#FFBC00] shadow-lg shadow-blue-100" : "text-gray-500 hover:text-[#FFBC00]"
                  }`}
                  onClick={() => setSelectedDeparture("no")}
                >
                  No
                </button>
              </div>
            </div>

            {/* Include nearby Arrival Airports */}

            <div className="flex flex-col gap-2">
              <label className="block mb-2 text-sm text-lightgray-200">Include nearby Arrival Airports</label>
              <div className="flex bg-lightblue-100 p-2 rounded-md overflow-hidden w-full -mt-2">
                <button
                  className={`p-2 rounded-md text-sm transition-colors w-full ${
                    selectedArrival === "yes" ? "bg-white text-[#FFBC00] shadow-lg shadow-blue-100" : "text-gray-500 hover:text-[#FFBC00]"
                  }`}
                  onClick={() => setSelectedArrival("yes")}
                >
                  Yes
                </button>
                <button
                  className={`p-2 rounded-md text-sm transition-colors w-full ${
                    selectedArrival === "no" ? "bg-white text-[#FFBC00] shadow-lg shadow-blue-100" : "text-gray-500 hover:text-[#FFBC00]"
                  }`}
                  onClick={() => setSelectedArrival("no")}
                >
                  No
                </button>
              </div>
            </div>
          </div>

          <div className="flex justify-end mb-6">
            <button
              className={`p-3 w-64 rounded-lg text-[#F5F9FE] text-center bg-[#FFBC00] text-sm tracking-wide flex items-center justify-center gap-2 ${
                isLoadingFlights ? "opacity-75 cursor-not-allowed" : ""
              }`}
              onClick={handleSearchClick}
              disabled={isLoadingFlights}
            >
              {isLoadingFlights ? (
                <>
                  <div className="w-5 h-5 border-2 border-[#F5F9FE] border-t-transparent rounded-full animate-spin"></div>
                  <span>SEARCHING...</span>
                </>
              ) : (
                "SEARCH"
              )}
            </button>
          </div>
        </div>
      </div>
      {searchResults}
    </div>
  );
};

export default SearchByRoute;
